import { useEffect, useRef, useState } from 'react'
import { getTaskStatus, TaskStatusResponse } from '../api/celeryClient'

export type TaskState = 'PENDING' | 'STARTED' | 'SUCCESS' | 'FAILURE' | 'IDLE'

export function useTaskResult(taskId?: string, opts?: { intervalMs?: number }) {
    const [state, setState] = useState<TaskState>(taskId ? 'PENDING' : 'IDLE')
    const [result, setResult] = useState<any | null>(null)
    const [error, setError] = useState<string | null>(null)
    const intervalRef = useRef<number | null>(null)
    const intervalMs = opts?.intervalMs ?? 1500

    useEffect(() => {
        if (!taskId) {
            setState('IDLE')
            setResult(null)
            setError(null)
            return
        }
        let cancelled = false
        setState('PENDING')
        setResult(null)
        setError(null)

        const poll = async () => {
            try {
                const data: TaskStatusResponse = await getTaskStatus(taskId)
                if (cancelled) return
                setState(data.state as TaskState)
                if (data.state === 'SUCCESS') {
                    setResult(data.result ?? null)
                    if (intervalRef.current)
                        window.clearInterval(intervalRef.current)
                } else if (data.state === 'FAILURE') {
                    setError(
                        (data.error && String(data.error)) || 'Task failed'
                    )
                    if (intervalRef.current)
                        window.clearInterval(intervalRef.current)
                }
            } catch (err: any) {
                if (cancelled) return
                setError(err?.message || 'Network error')
            }
        }

        poll()
        intervalRef.current = window.setInterval(poll, intervalMs)

        return () => {
            cancelled = true
            if (intervalRef.current) window.clearInterval(intervalRef.current)
        }
    }, [taskId, intervalMs])

    return { state, result, error }
}

export default useTaskResult
